import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import * as _ from 'lodash';
import moment from 'moment';

const SUBSCRIPTION = 'subscription';
const CONNECTION = 'connection';

const initialState = {
  selectedLocations: [],
  selectedLocationsWithCMFNumber: [],
  isCheckDataRightsSignedPending: false,
  isCheckAppTermsSignedPending: true,
  isCheckAddendumsSignedPending: false,
  isCheckLocationAlreadySubscribedToAppPending: true,
  consentInfo: {},
  alreadySubscribedLocations: [],
  pendingSubscriptionForLocations: [],
  savingAddendum: false,
  dataRightsLocationsToConsent: [],
  alreadySubscribedLocationsWithCMFNumber: [],
  headerStepsNotClickable : false,
  isCheckDataRightsSignedErrored: false,
  locationsToAddForSubManagement: [],
  locationsToAddForSubManagementWithCMF: [],
  appConsentDetails: [],
  addendumConsentDetails: [],
  isDataRightsAccepted : false,
  numberOfRequests: 0,
  alreadySubscribedCMFs: [],
  pendingSubscriptionCMFs: [],
  isCheckLocationAlreadySubscribedToAppFromBackendPending: true,
  locationsSubscribedFromBackend: [],
  checkAppTermsErroredEntityIds: [],
  checkAddendumsErroredEntityIds: [],
  checkDataRightsErroredEntityIds: [],
};

export const checkIsLocationAlreadySubscribedToApp = createAsyncThunk(
  'checkIsLocationAlreadySubscribedToApp',
  async ({ locations, solutionId, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: {
        entities: locations?.map(location => location?.entityId),
        solutionId
      },
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/subscriptions/entities?statusOnly=true`
    });
    return {
      response: {
        data: response?.data
      }
    };
  }
);

export const checkIsLocationAlreadySubscribedToAppFromBackend = createAsyncThunk(
  'checkIsLocationAlreadySubscribedToAppFromBackend',
  async ({ locations, solutionId, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      data: {
        entities: locations?.map(location => location?.entityId),
        solutionId
      },
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/subscriptions/entities?backendSubscriptionsOnly=true&summaryOnly=true`
    });
    return {
      locations,
      subscribedEntityIds: response?.data?.map(entity => entity?.entityId)
    };
  }
);

export const checkDataRightsSigned = createAsyncThunk(
  'checkDataRightsSigned',
  async ({ entityId, location, selectedLocationCMFNumber, token }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_MP_SALESFORCE_INTEGRATION}/salesforce/datarights/${selectedLocationCMFNumber}`
    });
    return {
      entityId,
      location,
      cmfNumber: selectedLocationCMFNumber,
      isDataRightsAccepted:
        (response &&
        response.data &&
        response.data.length &&
        response.data[0].Data_Rights_Consent__c) || null
    };
  }
);

export const checkAppTermsSigned = createAsyncThunk(
  'checkAppTermsSigned',
  async ({ solutionId, location, entityId, selectedLocationCMFNumber, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/consents/entities`,
      data: {
        entityId,
        solutionId,
        consentType: SUBSCRIPTION,
        statusOnly: true
      }
    });

    return {
      location,
      entityId,
      data: response.data === 'pending' ? false : (response && response.data && response.data.cmfNumber === selectedLocationCMFNumber && response.data) || false,
      cmfNumber: selectedLocationCMFNumber
    }}
);

export const checkAddendumsSigned = createAsyncThunk(
  'checkAddendumsSigned',
  async ({ solutionId, location, entityId, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/consents/entities`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: {
        entityId,
        solutionId,
        consentType: CONNECTION,
        statusOnly: true
      }
    });
    return {
      location,
      entityId,
      acceptedAddendums: response && response.data
    };
  }
);

export const selectedLocationsSlice = createSlice({
  name: 'selectedLocationsSlice',
  initialState,
  reducers: {
    addLocation: (state, action) => {
      let selectedLocations = state.selectedLocations;
      selectedLocations.push(action.payload);
      state.selectedLocations = selectedLocations;
      state.headerStepsNotClickable = true;
    },
    addLocationWithCMFNumber: (state, action) => {
      let selectedLocationsWithCMFData = state.selectedLocationsWithCMFNumber;
      selectedLocationsWithCMFData.push(action.payload);
      state.selectedLocationsWithCMFNumber = selectedLocationsWithCMFData;
      state.consentInfo = _.merge(
        {
          [action.payload.entityId]: {
            cmfNumber: action.payload.cmfNumber
          }
        },
        state.consentInfo
      );
    },
    removeLocation: (state, action) => {
      let selectedLocations = state.selectedLocations;
      selectedLocations =
        selectedLocations &&
        selectedLocations.filter(loc => loc !== action.payload);
      state.selectedLocations = selectedLocations;
      const appTermsErroredEntityIds = state.checkAppTermsErroredEntityIds?.filter(e => e !== action.payload);
      state.checkAppTermsErroredEntityIds = appTermsErroredEntityIds;
      const dataRightsErroredEntityIds = state.checkDataRightsErroredEntityIds?.filter(e => e != action.payload);
      state.checkDataRightsErroredEntityIds = dataRightsErroredEntityIds;
      const addendumsErroredEntityIds = state.checkAddendumsErroredEntityIds?.filter(e => e != action.payload);
      state.checkAddendumsErroredEntityIds = addendumsErroredEntityIds;
    },
    removeLocationWithCMFNumber: (state, action) => {
      let selectedLocationsWithCMFData = state.selectedLocationsWithCMFNumber;
      state.selectedLocationsWithCMFNumber =
        selectedLocationsWithCMFData &&
        selectedLocationsWithCMFData.filter(
          obj => obj.entityId !== action.payload
        );
    },
    addAllLocations: (state, action) => {
      state.selectedLocations = action.payload;
      state.headerStepsNotClickable = true;
    },
    addAllLocationsWithCMFNumber: (state, action) => {
      state.selectedLocationsWithCMFNumber = action.payload;
      let selectedLocationsWithCMFNumber = action.payload;
      selectedLocationsWithCMFNumber.forEach((locationObj)=>{
        state.consentInfo = _.merge(
          {
            [locationObj.entityId]: {
              cmfNumber: locationObj.cmfNumber
            }
          },
          state.consentInfo
        );
      })
    },
    removeAllLocations: state => {
      state.selectedLocations = [];
      state.headerStepsNotClickable = true;
    },
    removeAllLocationsWithCMFNumber: state => {
      state.selectedLocationsWithCMFNumber = [];
    },
    removeLocationFromConsentInfo: (state, action) => {
      let entityId = action.payload;
      delete state.consentInfo[entityId];
      let dataRightsLocationsToConsent = state.dataRightsLocationsToConsent;
      dataRightsLocationsToConsent =
        dataRightsLocationsToConsent &&
        dataRightsLocationsToConsent.filter(
          item => item.entityId !== entityId
        );
      state.dataRightsLocationsToConsent = dataRightsLocationsToConsent;
      state.headerStepsNotClickable = true;
      state.appConsentDetails = state.appConsentDetails.filter(item => item.entityId !== entityId);
      state.addendumConsentDetails = state.addendumConsentDetails.filter(item => item.entityId !== entityId);
    },
    removeAllLocationsFromConsentInfo: state => {
      state.consentInfo = {};
      state.dataRightsLocationsToConsent = [];
      state.appConsentDetails = []
      state.addendumConsentDetails = []
    },
    updateDataRightsAcceptedStatusInConsentInfo: state => {
      let dataRightsLocationsToConsent = state.dataRightsLocationsToConsent;
      // eslint-disable-next-line array-callback-return
      dataRightsLocationsToConsent.map(location => {
        state.consentInfo[location.entityId].dataRightsAccepted = 'Yes';
      });
      state.dataRightsLocationsToConsent = [];
    },
    makeHeaderStepsClickable : state => {
      state.headerStepsNotClickable = false;
    },
    addLocationsForSubManagement: (state, action) => {
      state.locationsToAddForSubManagement = action.payload;
    },
    addLocationsWithCMFForSubManagement: (state, action) => {
      state.locationsToAddForSubManagementWithCMF = action.payload;
      let locationsToAddForSubManagementWithCMF = action.payload;
      locationsToAddForSubManagementWithCMF.forEach((locationObj)=>{
        state.consentInfo = _.merge(
          {
            [locationObj.entityId]: {
              cmfNumber: locationObj.cmfNumber
            }
          },
          state.consentInfo
        );
      })
    },
    pauseLoadingStateOfSubscribedToAppPending: (state, action) => {
      state.isCheckLocationAlreadySubscribedToAppPending = false;
    },
    addConsentDetailsonTermsReview: (state, action) => {
      let addendumsInfo = action.payload;
      addendumsInfo.forEach(addendums => {
        let addendumsAccepted = [];
        addendums.consentDetails && addendums.consentDetails.length && addendums.consentDetails.forEach(item => {
          addendumsAccepted.push({
            apiProviderId: item.apiProviderId,
            documentLink : item.documentLink,
            signedTermsAndConditions : item.signedTermsAndConditions
          });
          state.addendumConsentDetails.push({
            apiProviderId: item.apiProviderId,
            entityId: addendums.entityId,
            location: addendums.location,
            name: item.name,
            createdDate: moment(item.createdDate).format('MM/DD/YYYY HH:mm:ss')
          })
        });
        state.consentInfo = _.merge(
          {
            [addendums.entityId]: {
              addendumsAccepted: [...state.consentInfo[addendums.entityId].addendumsAccepted, ...addendumsAccepted],
              entityId: addendums.entityId
            }
          },
          state.consentInfo
        );
      });
    },

    clearSelectLocations: state => {
      state.alreadySubscribedLocations = [];
      state.selectedLocations = [];
      state.selectedLocationsWithCMFNumber = [];
      state.consentInfo = {};
      state.pendingSubscriptionForLocations = [];
      state.dataRightsLocationsToConsent = [];
      state.alreadySubscribedLocationsWithCMFNumber = [];
      state.locationsToAddForSubManagement = [];
      state.isCheckLocationAlreadySubscribedToAppPending = true;
      state.locationsToAddForSubManagement = [];
      state.locationsToAddForSubManagementWithCMF = [];
      state.appConsentDetails = [];
      state.addendumConsentDetails = [];
      state.alreadySubscribedCMFs = [];
      state.pendingSubscriptionCMFs = [];
      state.isCheckLocationAlreadySubscribedToAppFromBackendPending = true;
      state.locationsSubscribedFromBackend = [];
    },
    updateDataRightsFlag : (state,action) =>{
      state.isDataRightsAccepted = action.payload
    }
  },
  extraReducers: {
    [checkDataRightsSigned.fulfilled]: (state, action) => {
      let dataRightsInfo = action.payload;
      if(state.selectedLocations.includes(dataRightsInfo.entityId) || state.locationsToAddForSubManagement.includes(dataRightsInfo.entityId)) {
      state.consentInfo = _.merge(
        {
          [dataRightsInfo.entityId]: {
            dataRightsAccepted: dataRightsInfo.isDataRightsAccepted
          }
        },
        state.consentInfo
      );
      let dataRightsLocationsToConsent = state.dataRightsLocationsToConsent;
      if (
        dataRightsLocationsToConsent &&
        (dataRightsInfo.isDataRightsAccepted === 'No' || dataRightsInfo.isDataRightsAccepted === null)
      ) {
        if (
          !dataRightsLocationsToConsent.find(
            item => item.cmfNumber === dataRightsInfo.cmfNumber && item.entityId === dataRightsInfo.entityId
          )
        ) {
          dataRightsLocationsToConsent.push({
            partyName: dataRightsInfo.location,
            entityId: dataRightsInfo.entityId,
            cmfNumber: dataRightsInfo.cmfNumber
          });
        }
      }
    }
    state.isCheckDataRightsSignedPending = false;
    state.isCheckDataRightsSignedErrored = false;
    },
    [checkDataRightsSigned.pending]: state => {
      state.isCheckDataRightsSignedPending = true;
      state.isCheckDataRightsSignedErrored = false;
    },
    [checkDataRightsSigned.rejected]: (state, action) => {
      const entityId = action.meta.arg.entityId;
      state.isCheckDataRightsSignedPending = false;
      state.isCheckDataRightsSignedErrored = true;
      state.checkDataRightsErroredEntityIds = [...state.checkDataRightsErroredEntityIds, entityId];
    },
    [checkAppTermsSigned.fulfilled]: (state, action) => {

      let appTermsInfo = action.payload;
      let newConsentInfo;
      let appConsentInfo;
      if(state.selectedLocations.includes(appTermsInfo.entityId) || state.locationsToAddForSubManagement.includes(appTermsInfo.entityId)) {
      newConsentInfo = _.merge(state.consentInfo, {
        [appTermsInfo.entityId]: {
          appTermsAccepted: appTermsInfo.data,
          entityId: appTermsInfo.entityId,
          location: appTermsInfo.location,
        }
      });
      state.consentInfo = newConsentInfo;

      if(appTermsInfo.data.name && appTermsInfo.data.createdDate){
        appConsentInfo = {
          entityId: appTermsInfo.entityId,
          location: appTermsInfo.location,
          name: appTermsInfo.data.name,
          createdDate: moment(appTermsInfo.data.createdDate).format('MM/DD/YYYY HH:mm:ss')
        };
      }
      if(appConsentInfo && (state.selectedLocations.includes(appConsentInfo.entityId) ||
          state.locationsToAddForSubManagement.includes(appConsentInfo.entityId)) &&
          !state.appConsentDetails.some(item => item.entityId === appConsentInfo.entityId)) {
        state.appConsentDetails.push(appConsentInfo);
      }

    }
    state.isCheckAppTermsSignedPending = false;
    },
    [checkAppTermsSigned.pending]: state => {
      state.isCheckAppTermsSignedPending = true;
    },
    [checkAppTermsSigned.rejected]: (state, action) => {
      const entityId = action.meta.arg.entityId;
      state.isCheckAppTermsSignedPending = false;
      state.checkAppTermsErroredEntityIds = [...state.checkAppTermsErroredEntityIds, entityId];
    },
    [checkAddendumsSigned.fulfilled]: (state, action) => {
      let addendumsInfo = action.payload;
      let addendums = action.payload.acceptedAddendums;
      let addendumsAccepted = [];
      let consentDetails = []
      addendums.forEach(item => {
        if (item.consentId) {
          addendumsAccepted.push({
            apiProviderId: item.apiProviderId,
            documentLink : item.documentLink,
            signedTermsAndConditions : item.signedTermsAndConditions
          });
        }
      });
      if(state.selectedLocations.includes(addendumsInfo.entityId) || state.locationsToAddForSubManagement.includes(addendumsInfo.entityId)) {
      state.consentInfo = _.merge(
        {
          [addendumsInfo.entityId]: {
            addendumsAccepted: [],
            entityId: addendumsInfo.entityId
          }
        },
        state.consentInfo
      );
      addendumsAccepted.forEach((item) => {
        if (!state.consentInfo[addendumsInfo.entityId].addendumsAccepted.some(element => element.apiProviderId === item.apiProviderId))
          state.consentInfo[addendumsInfo.entityId].addendumsAccepted.push(item);
      });

      addendums.forEach(item => {
        if (item.name && item.createdDate) {
          consentDetails = {
            apiProviderId: item.apiProviderId,
            entityId: addendumsInfo.entityId,
            location: addendumsInfo.location,
            name: item.name,
            createdDate: moment(item.createdDate).format('MM/DD/YYYY HH:mm:ss')
          };
        }
        if (!state.addendumConsentDetails.some(item => item.entityId === consentDetails.entityId && item.apiProviderId === consentDetails.apiProviderId)) {
          state.addendumConsentDetails.push(consentDetails);
        }
      })
    }
    state.isCheckAddendumsSignedPending = false;
    },
    [checkAddendumsSigned.pending]: state => {
      state.isCheckAddendumsSignedPending = true;
    },
    [checkAddendumsSigned.rejected]: (state, action) => {
      const entityId = action.meta.arg.entityId;
      state.isCheckAddendumsSignedPending = false;
      state.checkAddendumsErroredEntityIds = [...state.checkAddendumsErroredEntityIds, entityId];
    },
    [checkIsLocationAlreadySubscribedToApp.fulfilled]: (state, action) => {
      state.numberOfRequests -= 1;
      state.isCheckLocationAlreadySubscribedToAppPending = false;
      
      const entitiesSubscriptionData = action.payload?.response?.data || [];
      
      entitiesSubscriptionData.forEach(({ entityId, subscriptionData }) => {
        let data = subscriptionData;
        let isSubscribedAuto =
          data && data.length && data.some(type => type.status === 'subscribed' && type.appSubscriptionActivationType !== 'Manual');
        let isSubscribedManual =
          data && data.length && data.some(type => type.appSubscriptionActivationState === 'Active' && type.appSubscriptionActivationType === 'Manual' && type.status === 'subscribed' );
        let subscribedCMF = data && data.length && data.filter(type => type.status === 'subscribed');
        subscribedCMF = subscribedCMF && subscribedCMF.length && subscribedCMF[0].entitycmfNumber;
        if (isSubscribedAuto || isSubscribedManual) {
          if (!state.alreadySubscribedLocations.includes(entityId)) {
              state.alreadySubscribedLocations = [...state.alreadySubscribedLocations, entityId];
              state.alreadySubscribedCMFs = [...state.alreadySubscribedCMFs, subscribedCMF];
            }
        }
        let isPendingAuto = data && data.length && data.some(type => (type.status === 'pending' || !type.hasOwnProperty('status')) && type.requested_status === 'subscribe' && type.appSubscriptionActivationType !== 'Manual');
        let isPendingManual = data && data.length && data.some(type => (type.status === 'pending' || type.appSubscriptionActivationState === 'Pending' || !type.hasOwnProperty('status')) && type.requested_status === 'subscribe' && type.appSubscriptionActivationType === 'Manual');
        let pendingCMF = data && data.length && data.filter(type => type.requested_status === 'subscribe');
        pendingCMF = pendingCMF && pendingCMF.length && pendingCMF[0].entitycmfNumber;
        if (isPendingAuto || isPendingManual) {
          if (!state.pendingSubscriptionForLocations.includes(entityId)) {
            state.pendingSubscriptionForLocations = [...state.pendingSubscriptionForLocations, entityId];
            state.pendingSubscriptionCMFs = [...state.pendingSubscriptionCMFs, pendingCMF];
          }
        }
      });
    },
    [checkIsLocationAlreadySubscribedToApp.pending]: state => {
      state.numberOfRequests += 1;
      state.isCheckLocationAlreadySubscribedToAppPending = true;
    },
    [checkIsLocationAlreadySubscribedToApp.rejected]: state => {
      state.numberOfRequests -= 1;
      state.isCheckLocationAlreadySubscribedToAppPending = false;
    },
    [checkIsLocationAlreadySubscribedToAppFromBackend.fulfilled]: (state, action) => {
      const subscribedEntityIds = action?.payload?.subscribedEntityIds;
      state.numberOfRequests -= 1;
      state.isCheckLocationAlreadySubscribedToAppFromBackendPending = false;
      state.locationsSubscribedFromBackend.push(...subscribedEntityIds);
    },
    [checkIsLocationAlreadySubscribedToAppFromBackend.pending]: state => {
      state.numberOfRequests += 1;
      state.isCheckLocationAlreadySubscribedToAppFromBackendPending = true;
    },
    [checkIsLocationAlreadySubscribedToAppFromBackend.rejected]: state => {
      state.numberOfRequests -= 1;
      state.isCheckLocationAlreadySubscribedToAppFromBackendPending = false;
    }
  }
});

export const {
  addLocation,
  addLocationWithCMFNumber,
  removeLocation,
  removeLocationWithCMFNumber,
  addAllLocations,
  addAllLocationsWithCMFNumber,
  removeAllLocations,
  removeAllLocationsWithCMFNumber,
  removeLocationFromConsentInfo,
  removeAllLocationsFromConsentInfo,
  updateDataRightsAcceptedStatusInConsentInfo,
  makeHeaderStepsClickable,
  addLocationsForSubManagement,
  clearSelectLocations,
  addLocationsWithCMFForSubManagement,
  updateDataRightsFlag,
  pauseLoadingStateOfSubscribedToAppPending,
  addConsentDetailsonTermsReview,
} = selectedLocationsSlice.actions;

export default selectedLocationsSlice.reducer;
